import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap, Sparkles, Globe, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, LANGUAGES } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { APP_LOGIN_URL, APP_REGISTER_URL } from "@/lib/config";

const NAV_LINKS = [
  { tid: "solutions", ckey: "nav_solutions", id: "features" },
  { tid: "templates", ckey: "nav_templates", id: "templates" },
  { tid: "api", ckey: "nav_api", id: "more" },
  { tid: "pricing", ckey: "nav_pricing", id: "pricing" },
  { tid: "contact-us", ckey: "nav_contact", id: "contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, setLang } = useLanguage();
  const { t } = useSiteContent();
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="main-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
        >
          <img src="/logo.png" alt="Logo Iris Photo" className="h-9 w-9 rounded-xl object-cover" />
          <span className="text-lg font-semibold tracking-tight text-white">Extracción Forense Iris Photo</span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l, i) => (
            <button
              key={l.tid + i}
              data-testid={`nav-link-${l.tid}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm text-white/70 hover:text-white transition-colors duration-200"
            >
              {t(l.ckey)}
            </button>
          ))}
          <a
            href="/upscale"
            data-testid="nav-link-upscale"
            className="relative flex items-center gap-1.5 h-9 px-4 rounded-full bg-gradient-to-r from-indigo-500/20 to-violet-500/20 border border-indigo-400/40 text-white hover:from-indigo-500/30 hover:to-violet-500/30 transition-colors text-sm font-medium"
          >
            <Sparkles className="h-4 w-4 text-indigo-300" /> {t('nav_upscale')}
            <span className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-[9px] font-bold uppercase">Nuevo</span>
          </a>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="lang-selector-button"
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="flex items-center gap-2 h-9 px-3 rounded-full text-white/80 hover:text-white hover:bg-white/5 transition-colors text-sm"
            >
              <Globe className="h-4 w-4" /> {current.flag} {current.code.toUpperCase()}
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="absolute right-0 mt-2 w-44 glass rounded-2xl p-2 z-50"
                  data-testid="lang-menu"
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onMouseDown={() => { setLang(l.code); setLangOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-white/80 hover:bg-white/10 transition-colors"
                    >
                      <span>{l.flag}</span> <span className="flex-1 text-left">{l.label}</span>
                      {l.code === lang && <Check className="h-4 w-4 text-indigo-400" />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Button
            data-testid="header-trial-button"
            onClick={() => { window.location.href = APP_REGISTER_URL; }}
            className="rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white px-5 gap-2 border-0"
          >
            <Zap className="h-4 w-4" /> {t('nav_trial')}
          </Button>
          <Button
            data-testid="header-login-button"
            variant="ghost"
            onClick={() => { window.location.href = APP_LOGIN_URL; }}
            className="rounded-full text-white/80 hover:text-white hover:bg-white/5"
          >
            {t('nav_login')}
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-white/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV_LINKS.map((l, i) => (
                <button
                  key={l.tid + i}
                  data-testid={`mobile-nav-${l.tid}`}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left text-white/80 py-1"
                >
                  {t(l.ckey)}
                </button>
              ))}
              <Button
                data-testid="mobile-trial-button"
                onClick={() => { window.location.href = APP_REGISTER_URL; }}
                className="rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white w-full mt-2"
              >
                {t('nav_trial')}
              </Button>
              <button
                data-testid="mobile-login-link"
                onClick={() => { window.location.href = APP_LOGIN_URL; }}
                className="text-left text-white/80 py-1"
              >
                {t('nav_login')}
              </button>
              <a href="/upscale" data-testid="mobile-nav-upscale" className="text-indigo-300 py-1 flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> {t('nav_upscale')}
              </a>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10 mt-2">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => { setLang(l.code); setOpen(false); }}
                    className={`px-3 py-1.5 rounded-full text-sm ${l.code === lang ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-white" : "bg-white/5 text-white/70"}`}
                  >
                    {l.flag} {l.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
